import axios from 'utils/axios'
import { ApiError } from 'api/errors'
import { User } from 'types'
import type { SignupAttribution } from 'utils/attribution'

function signUpFriendlyMessage(code?: string): string {
  return code === 'UsernameExistsException'
    ? 'An account with this email already exists. Please log in.'
    : 'Sign up failed. Please try again.'
}

export async function getCurrentUser(): Promise<User> {
  const response = await axios.get('user/me')

  return response.data as User
}

export async function signUp(params: {
  email: string
  password: string
  signup_token?: string
  recaptcha_token?: string
  marketing_opt_in?: boolean
  referrer_code?: string
  user_name?: string
  // Which link and channel brought them (utils/attribution.ts); the backend
  // records it while its referral_attribution switch is on and ignores it
  // otherwise. The code itself travels as referrer_code.
  attribution?: SignupAttribution
}): Promise<{ userSub: string; userConfirmed: boolean }> {
  // Serverless /auth/signup: flat body, creates the Cognito user AND the backing
  // app `users` row, and auto-confirms (no email code). A fail (e.g. duplicate
  // email) is a HTTP-200 { status:false } envelope, which the response
  // interceptor now REJECTS as an ApiError carrying the Cognito error `code`.
  // Map that code to a friendly message; on success the envelope unwraps to
  // { userSub, userConfirmed }.
  let response
  try {
    response = await axios.post('/auth/signup', {
      user_email: params.email,
      user_name: params.user_name ?? '',
      password: params.password,
      // recaptcha_token is intentionally NOT forwarded: serverless /auth/signup
      // ignores it. axios omits undefined values, so unset optional fields below
      // simply aren't serialized.
      referrer_code: params.referrer_code,
      marketing_opt_in: params.marketing_opt_in,
      signup_token: params.signup_token,
      attribution: params.attribution
    })
  } catch (err) {
    const code = err instanceof ApiError ? err.code : undefined
    const message = signUpFriendlyMessage(code)
    const friendly = new Error(message) as Error & { friendlyMessage?: string }
    friendly.friendlyMessage = message
    throw friendly
  }

  const data = response.data as {
    userSub?: string
    userConfirmed?: boolean
    code?: string
  }

  if (!data?.userSub) {
    const message = signUpFriendlyMessage(data?.code)
    const err = new Error(message) as Error & { friendlyMessage?: string }
    err.friendlyMessage = message
    throw err
  }

  return { userSub: data.userSub, userConfirmed: !!data.userConfirmed }
}

export async function confirmEmail({
  email,
  confirmation_token,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  recaptcha_token: _recaptcha_token
}: {
  email: string
  confirmation_token: string
  recaptcha_token?: string
}): Promise<void> {
  await axios.post('/auth/confirm-signup', {
    email,
    confirmationCode: confirmation_token
  })
}

export async function resendConfirmationEmail({
  email,
  recaptcha_token
}: {
  email: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  const response = await axios.post('/auth/resend-code', {
    email,
    recaptcha_token
  })

  return response.data
}

export async function requestResetPassword({
  email
}: {
  email: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  // Serverless: POST /auth/forgot-password { email } emails a 6-digit reset
  // code (the non-existent /user/passwords endpoint never did anything).
  await axios.post('/auth/forgot-password', { email })

  return { status: 'ok' }
}

export async function resetPassword({
  email,
  confirmationCode,
  newPassword
}: {
  email: string
  confirmationCode: string
  newPassword: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  // Serverless: POST /auth/reset-password { email, confirmationCode,
  // newPassword } completes the Cognito ForgotPassword flow. Requires the
  // emailed 6-digit code — there is no reset link token.
  await axios.post('/auth/reset-password', {
    email,
    confirmationCode,
    newPassword
  })

  return { status: 'ok' }
}

export async function createUserInvite(params: {
  name: string
  email: string
  recaptcha_token?: string
  utm_id?: string
}): Promise<unknown> {
  const { recaptcha_token, utm_id, ...user_invite } = params

  const response = await axios.post('user_invites', {
    user_invite,
    recaptcha_token,
    utm_id
  })

  return response.data
}
